// frontend/src/pages/edit/Edit.jsx

import React, { useState, useEffect, useContext, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../context/Context";
import axios from "axios";
import { toast } from "react-toastify";
import "./edit.css";
import { jwtDecode } from "jwt-decode";
import DraftCardModal from "../../components/modals/DraftCardModal";
import { getDaysRemaining } from "../../utils/dueDates";
import {
  canAccessDraft,
  canManageStage,
  canCommentOnDraft,
  isAdmin,
} from "../../utils/permissions";
import {
  STAGE_ORDER,
  toHuman,
  toKey,
  nextStage,
  prevStage,
  buildSections,
  WRITER_STAGES,
  EDITOR_STAGES,
  LEAD_EDITOR_STAGES
} from "../../utils/stages";

const UnauthorizedComponent = () => (
  <div style={{ textAlign: "center", paddingTop: "100px", fontSize: "18px" }}>
    Not Authorized!
  </div>
);

const SECTIONS = buildSections();

const getRoleLabelForSection = (title) => {
  const key = toKey(title); // convert "Copy Edit" → "copy_edit" etc.

  if (WRITER_STAGES.has(key)) return "Writers";
  if (EDITOR_STAGES.has(key)) return "Editors";
  if (LEAD_EDITOR_STAGES.has(key)) return "Lead Editor";
  return null;
};

const ArticleRow = ({ color, draft, onOpen, onStageChange = () => {}, user, onOpenInWriter }) => {
  const currentKey = draft.stage || toKey(draft.stageTitle);
  const atStart = currentKey === STAGE_ORDER[0];
  const atEnd = currentKey === STAGE_ORDER[STAGE_ORDER.length - 1];
  const canOpen = canAccessDraft(draft, user);

  return (
    <div
      className="article-row"
      onClick={() => onOpen?.(draft)}
      style={{ cursor: "pointer" }}
    >
      <div className="cell-title">
        <div className="dot" style={{ backgroundColor: color }} />
        <span className="article-title">{draft?.title}</span>
      </div>

      <div className="cell-author">
        <span className="article-title">{draft?.authorName || "-"}</span>
      </div>

      <div className="cell-editor">
        <span className="article-title">{draft?.editorName || "-"}</span>
      </div>

      <div className="cell-due">
        {draft?.finalDraftDue ? (
          <>
            <span className="due-date">
              {new Date(draft.finalDraftDue).toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>

            {(() => {
              const remaining = getDaysRemaining(draft.finalDraftDue);
              if (!remaining) return null;

              const { text, color } = remaining;

              const background =
                color === "green"
                  ? "rgba(0, 128, 0, 0.1)"
                  : color === "orange"
                  ? "rgba(255, 165, 0, 0.15)"
                  : "rgba(255, 0, 0, 0.1)";

              return (
                <span
                  className="due-badge"
                  style={{
                    color,
                    background,
                    border: `1px solid ${color}`,
                  }}
                >
                  {text}
                </span>
              );
            })()}
          </>
        ) : (
          <span className="due-empty">—</span>
        )}
      </div>

      {canManageStage(draft, user) && (
        <div className="cell-stage-controls">
          <button
            className="btn-ghost sm"
            disabled={atStart}
            onClick={(e) => { e.stopPropagation(); onStageChange(draft._id, prevStage(currentKey)); }}
            title="Move to previous stage"
          >
            🔼
          </button>

          <button
            className="btn-ghost sm"
            disabled={atEnd}
            onClick={(e) => { e.stopPropagation(); onStageChange(draft._id, nextStage(currentKey)); }}
            title="Move to next stage"
          >
            🔽
          </button>
        </div>
      )}

      <div className="cell-actions">
        {canOpen ? (
          <button
            className="btn-primary"
            title={canOpen ? "Open in The Writer’s Room" : "You don’t have access"}
            onClick={(e) => {
              e.stopPropagation();
              if (canOpen) onOpenInWriter?.(draft);
            }}
          >
            <span className="icon">✎</span> Open in Writer's Room
          </button>
        ) : (
          <span className="lock-indicator" title="Only article owners (writers and editors) and admins can open this in Writer’s Room">🔒</span>
        )}
      </div>
    </div>
  );
};

export default function Edit() {
  const { user, token } = useContext(Context);
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const pollRef = useRef(null);
  const inFlightRef = useRef(false);

  const [activeDraft, setActiveDraft] = useState(null);
  const openModal = (draft) => setActiveDraft(draft);
  const closeModal = () => setActiveDraft(null);

  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(SECTIONS.map(s => [s.title, true])) // all open by default
  );

  const toggleSection = (title) =>
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));

  const setAll = (val) => {
    setOpenSections(Object.fromEntries(SECTIONS.map(s => [s.title, val])));
  };

  const allOpen = Object.values(openSections).every(Boolean);
  const allClosed = Object.values(openSections).every(v => !v);

  const loadArticles = useCallback(async () => {
    if (!user || !token) return;

    // Prevent overlapping requests (important with polling + user actions)
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const url = isAdmin(user) ? "/posts?all=true" : "/posts";
      
      const articlesRes = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const allArticles = articlesRes.data;

      const visibleDrafts = allArticles.filter(
        (a) => a.status === "draft"
      );

      
      const normalizedDrafts = visibleDrafts.map(d => ({
        ...d,
        stageTitle: toHuman(d.stage), // convert backend value to display name
        postSlug: d.post_slug,
      }));
      
      setDrafts(normalizedDrafts);
    
    } catch (err) {
      console.error("Error fetching articles", err);
    } finally {
      inFlightRef.current = false;
    }
  }, [user, token]);

  const updateStage = async (draftId, newStageKey) => {
    // optimistic local update (instant UI)
    setDrafts(prev =>
      prev.map(d =>
        d._id === draftId
          ? { ...d, stage: newStageKey, stageTitle: toHuman(newStageKey) }
          : d
      )
    );

    try {
      await axios.put(`/posts/${draftId}`, { stage: newStageKey }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Keep fully in sync (assigned editor, updatedAt, etc.)
      loadArticles();
    } catch (e) {
      console.error("Stage update failed", e);
      // revert on failure
      setDrafts(prev => prev.map(d => {
        if (d._id !== draftId) return d;
        // fallback: recompute from existing d.stage (if you keep it) or force to Ideation
        const fallbackKey = toKey(d.stageTitle);
        return { ...d, stage: fallbackKey, stageTitle: toHuman(fallbackKey) };
      }));
    }
  };

  const addComment = async (draftId, text) => {
    const d = drafts.find(x => x._id === draftId);
    if (!canCommentOnDraft(d, user)) return toast?.error?.("No access.");

    const res = await axios.post(
      `/posts/${draftId}/comments`,
      { text },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const newComment = res.data;

    // update activeDraft (modal) if it’s the same draft
    setActiveDraft(prev =>
      prev && prev._id === draftId
        ? { ...prev, comments: [...(prev.comments || []), newComment] }
        : prev
    );

    // also update in the main list so it stays in sync
    setDrafts(prev =>
      prev.map(d =>
        d._id === draftId
          ? { ...d, comments: [...(d.comments || []), newComment] }
          : d
      )
    );
  };

  const deleteComment = async (draftId, commentId) => {
    const d = drafts.find(x => x._id === draftId);
    if (!canCommentOnDraft(d, user)) return toast?.error?.("No access.");

    // optimistic UI
    setActiveDraft(prev =>
      prev && prev._id === draftId
        ? { ...prev, comments: (prev.comments || []).filter(c => c._id !== commentId) }
        : prev
    );
    setDrafts(prev =>
      prev.map(d => d._id === draftId
        ? { ...d, comments: (d.comments || []).filter(c => c._id !== commentId) }
        : d)
    );

    try {
      await axios.delete(`/posts/${draftId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // rollback if server fails
      await loadArticles();
      console.error("Delete comment failed", e);
      toast?.error?.("Couldn't delete comment.");
    }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate("/login");
    }

    try {
      const userRole = Number(jwtDecode(token)?.role);
      setIsAuthorized(userRole > 1);
    } catch (err) {
      console.error("Invalid token", err);
      setIsAuthorized(false);
    }
  }, [user, token, navigate]);

  useEffect(() => {
    if (isAuthorized) {
      loadArticles();
    }
  }, [isAuthorized, loadArticles]);

  // Poll for cross-user stage updates (no websockets)
  useEffect(() => {
    if (!isAuthorized) return;

    const start = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        // Only poll when tab is visible
        if (!document.hidden) loadArticles();
      }, 10000); // 10s: "close to real time"
    };

    const stop = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else {
        // catch up immediately when user returns
        loadArticles();
        start();
      }
    };

    // initial start
    start();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isAuthorized, loadArticles]);

  const draftsBySection = React.useMemo(() => {
    // create an empty array for each section
    const map = Object.fromEntries(SECTIONS.map(s => [s.title, []]));

    // put each draft into its matching section
    for (const d of drafts) {
      const key = d.stageTitle;
      (map[key] ?? (map["Ideation"] ||= [])).push(d);
    }

    // optional: newest first
    Object.keys(map).forEach(k =>
      map[k].sort((a,b) => new Date(b.updatedAt||0) - new Date(a.updatedAt||0))
    );

    return map;
  }, [drafts]);

  const pickSlug = (d) =>
    d?.post_slug ??
    d?.postSlug ??
    d?.draftSlug ??
    d?.slug ??
    d?.tempSlug ??
    null;

  const openInWritersRoom = (draft) => {
    if (!draft) return;

    if (!canAccessDraft(draft, user)) {
      toast?.error?.("You don’t have access to this draft.");
      return;
    }

    const slug = pickSlug(draft);
    if (slug) {
      navigate(`/write?draft=${encodeURIComponent(slug)}`);
    } else if (draft._id) {
      navigate(`/write?postId=${encodeURIComponent(draft._id)}`);
    }
  };

  if (!isAuthorized) {
    return <UnauthorizedComponent />;
  }

  return (
   <>
    <div className="Edit">
      <div className="edit">
        <div
          className="write-title"
          style={{ position: "relative", display: "flex", alignItems: "center", gap: "40px" }}
        >
          <button
            style={{
              width: "190px",
              height: "32px",
              borderRadius: "10px",
              backgroundColor: "#B1E5F2",
              color: "#272635",
              border: "none",
              cursor: "pointer",
              fontSize: "15px",
              marginLeft: "75px",
            }}
            onClick={() => navigate("/write")}
          >
  <span style={{ display: "inline-block", transform: "scaleX(-1)" }}>
    &#x279C;
  </span> 
            &nbsp;THE WRITER'S ROOM
          </button>
          <p
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              margin: 0,
            }}
          >
            Editorial Dashboard
          </p>
        </div>
        <div className="edit-body">

          <div
            style={{
              width: "100%",
              maxWidth: "960px",
              display: "flex",
              justifyContent: "flex-start"
            }}
          >
            {!allClosed && (
              <button
                className="btn-ghost"
                onClick={() => setAll(false)}
                disabled={allClosed}
                aria-disabled={allClosed}
                title={allClosed ? "All sections are already collapsed" : "Collapse all sections"}
              >
                Collapse All
              </button>
            )}
            {!allOpen && (
              <button
                className="btn-ghost"
                onClick={() => setAll(true)}
                disabled={allOpen}
                aria-disabled={allOpen}
                title={allOpen ? "All sections are already expanded" : "Expand all sections"}
              >
                Expand All
              </button>
            )}
          </div>

          {SECTIONS.map(({ title, color, items }) => {

            const roleLabel = getRoleLabelForSection(title);

            return (
              <div key={title} className="edit-section">

                <div className="edit-section-columns">
                  <div
                    className="edit-section-inner-container"
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSection(title)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSection(title)}
                    aria-expanded={!!openSections[title]}
                    aria-controls={`section-${title}`}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="edit-section-header"
                      style={{
                        borderRadius: openSections[title]
                          ? "12px 12px 0 0"
                          : "12px",
                        cursor: "pointer"
                      }}
                    >
                      <span
                        className="edit-section-dot"
                        style={{ backgroundColor: color }}
                      ></span>

                      <span className="edit-section-title-wrap">
                        <span className="edit-section-title">
                          {title}
                        </span>
                        {roleLabel && (
                          <span className="edit-section-role">
                            {roleLabel}
                          </span>
                        )}
                      </span>

                      <span
                        className="edit-section-chevron"
                        style={{
                          display: "inline-block",
                          transform: openSections[title] ? "rotate(0deg)" : "rotate(-180deg)",
                        }}
                      >
                        ⌃
                      </span>
                    </div>
                  </div>

                  {draftsBySection[title].length !== 0 && (
                    <>
                      <span className="col-label col-author">Author</span>
                      <span className="col-label col-editor">Editor</span>
                      <span className="col-label col-due">Final Draft Due</span>
                      <span className="col-label col-stage"></span>
                      <span className="col-label col-actions"></span>
                    </>
                  )}
                </div>

                {openSections[title] && (
                  <div className="edit-section-card">
                    {draftsBySection[title].length === 0 ? (
                      <div className="empty-state" style={{ fontWeight: 400 }}>
                        No drafts yet.
                      </div>
                    ) : (
                      draftsBySection[title].map((d) => (
                        <ArticleRow
                          key={d._id}
                          color={color}
                          draft={d}
                          onOpen={openModal}
                          user={user}
                          onStageChange={updateStage}
                          onOpenInWriter={openInWritersRoom}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <DraftCardModal
        open={!!activeDraft}
        onClose={closeModal}
        draft={activeDraft}
        onAddComment={addComment}
        onDeleteComment={deleteComment}
        currentUser={user}
        onOpenInWriter={openInWritersRoom}
      />

    </div>
   </>
  );
}
